/**
 * Agent Activity Page (admin + executive assistant only)
 *
 * Real-time window into Mac-mini agent ↔ CRM traffic:
 *   - Stream: full-width live brain-flow canvas (Intake → gbrain → unified
 *     agent → Delivery) with glowing transaction paths, edge hover/tap
 *     detail, and inline card tickers — scoped by a universal lens
 *   - Coordination: agent-reported CRM change requests + GitHub PR history
 *   - Directory: every agent function with a plain-language description,
 *     overlaid with calls-this-month so unused capabilities are visible
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db, app } from '../utils/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../utils/AuthContext';
import { useCRM } from '../utils/CRMContext';
import { canViewAgentActivity } from '../utils/agentActivityAccess';
import { resolveDisplayAgentId } from '../utils/agentActivityMapping';
import {
  DOMAIN_LABELS,
  humanizeEvent,
  humanizeQueueRequest,
  isPollEvent,
  type ActivityDomain,
  type ActivityKind,
} from '../utils/agentActivityHumanize';
import {
  hasReportedSkill,
  isMissingSkill,
  reportedSkillIds,
  resolveSkillId,
  skillById,
} from '../utils/contextFlowMapping';
import AppHeader from '../components/ui/AppHeader';
import BrainContextFlow, {
  itemMatchesLens,
  type FlowFeedItem,
  type FlowLens,
  type FlowSkillState,
} from '../components/agents/BrainContextFlow';
import {
  listGbrainSkills,
  skillsMapFromResult,
  type GbrainSkillRef,
} from '../utils/gbrainSkillsApi';
import {
  groupHitsBySection,
  kindLabel as brainKindLabel,
  searchCompanyBrain,
  type CompanyBrainHit,
  type CompanyBrainSection,
  type CompanyBrainSubject,
} from '../utils/companyBrainSearchApi';
import {
  Activity,
  AlertTriangle,
  Calendar,
  BookOpen,
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Copy,
  DollarSign,
  ExternalLink,
  Gavel,
  GitPullRequest,
  Handshake,
  FolderOpen,
  HardHat,
  Inbox,
  Landmark,
  LayoutList,
  Megaphone,
  Monitor,
  NotebookPen,
  Plane,
  Radio,
  Search,
  Sparkles,
  TrendingUp,
  UserPlus,
  X,
  ShoppingCart,
} from 'lucide-react';
import '../components/pm/pm-shared.css';
import './OperationsHub.css';
import './AgentActivityPage.css';

// ── Constants ──────────────────────────────────────────────

const ACTIVITY_COLLECTION = 'agentApiActivity';
const STATS_COLLECTION = 'agentActivityStats';
const USAGE_MONTHLY_COLLECTION = 'agentUsageMonthly';
const CRM_BUILDS_COLLECTION = 'agentCrmChangeRequests';
const CATALOG_URL = '/docs/agents/catalog.json';
const DOCS_INDEX_URL = '/docs/agents/docs-index.json';
const REPORTING_DOC_URL = '/docs/AGENT_REPORTING.md';
const PUBLIC_PLANNER_INSTRUCTION_URL =
  'https://clients.lctimberframes.com/docs/agents/travelPlannerInput.json';
/** Live activity window feeding the brain-flow canvas. */
const GLOBAL_FEED_LIMIT = 150;

interface AgentMeta {
  label: string;
  icon: React.ReactNode;
  accent: string;
}

/** Presentation metadata — accents/icons consistent with the Agent Harness board. */
const AGENT_META: Record<string, AgentMeta> = {
  calendarAgent: { label: 'Calendar', icon: <Calendar size={16} />, accent: '#3D5A40' },
  filesAgent: { label: 'Files', icon: <FolderOpen size={16} />, accent: '#4F6172' },
  marketingAgent: { label: 'Marketing', icon: <Megaphone size={16} />, accent: '#7C5A4A' },
  biddingAgent: { label: 'Bidding', icon: <Gavel size={16} />, accent: '#9A6A2F' },
  dealsAgent: { label: 'Deals', icon: <Handshake size={16} />, accent: '#5B6E4A' },
  projectsAgent: { label: 'Projects', icon: <HardHat size={16} />, accent: '#6B5B4A' },
  meetingNotesAgent: { label: 'Meeting Notes', icon: <NotebookPen size={16} />, accent: '#4A6670' },
  projectMeetingsAgent: { label: 'Project Meetings', icon: <NotebookPen size={16} />, accent: '#3D5A4C' },
  portalActivityAgent: { label: 'Portal Activity', icon: <Activity size={16} />, accent: '#B45309' },
  opsTasksAgent: { label: 'OPS Tasks', icon: <ClipboardList size={16} />, accent: '#5C6B4A' },
  salesAgent: { label: 'Sales', icon: <TrendingUp size={16} />, accent: '#6B4C9A' },
  travelAgent: { label: 'Travel', icon: <Plane size={16} />, accent: '#3A6B7C' },
  financeAgent: { label: 'Finance', icon: <Landmark size={16} />, accent: '#3D5A40' },
  purchasingAgent: { label: 'Purchasing', icon: <ShoppingCart size={16} />, accent: '#7C5A4A' },
  execInboxAgent: { label: 'Exec Inbox', icon: <Inbox size={16} />, accent: '#2C3E2F' },
  agentGateway: { label: 'Gateway', icon: <Building2 size={16} />, accent: '#6B6960' },
  gateway: { label: 'Gateway', icon: <Building2 size={16} />, accent: '#6B6960' },
  crmApi: { label: 'CRM Lead API', icon: <UserPlus size={16} />, accent: '#2563EB' },
  companyBrain: { label: 'Company Brain', icon: <Sparkles size={16} />, accent: '#444444' },
  gbrain: { label: 'Unified Agent', icon: <Sparkles size={16} />, accent: '#1A1A1A' },
};

const FALLBACK_META: AgentMeta = { label: 'Agent', icon: <Sparkles size={16} />, accent: '#6B6960' };

function metaFor(agentId: string): AgentMeta {
  return AGENT_META[agentId] ?? { ...FALLBACK_META, label: agentId };
}

function skillStatesMapsEqual(
  a: Map<string, FlowSkillState>,
  b: Map<string, FlowSkillState>,
): boolean {
  if (a === b) return true;
  if (a.size !== b.size) return false;
  for (const [id, st] of Array.from(b.entries())) {
    const prev = a.get(id);
    if (!prev) return false;
    if (
      prev.working !== st.working ||
      prev.lastSentence !== st.lastSentence ||
      prev.withName !== st.withName ||
      (prev.lastTs?.toMillis() || 0) !== (st.lastTs?.toMillis() || 0)
    ) {
      return false;
    }
  }
  return true;
}

/** Meaningful canvas rows — ignore poll-only snapshot noise. */
function flowItemsSignatureEqual(a: FlowFeedItem[], b: FlowFeedItem[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const x = a[i];
    const y = b[i];
    if (
      x.id !== y.id ||
      x.status !== y.status ||
      x.sentence !== y.sentence ||
      (x.ts?.toMillis() || 0) !== (y.ts?.toMillis() || 0)
    ) {
      return false;
    }
  }
  return true;
}

// ── Types ──────────────────────────────────────────────────

interface ActivityEvent {
  id: string;
  agentId: string;
  apiAgentId?: string | null;
  action: string;
  source: 'direct' | 'gateway' | 'callable' | 'chat' | 'reported';
  status: 'ok' | 'denied' | 'error';
  httpStatus: number;
  latencyMs: number;
  keyKind: string;
  host?: string | null;
  paramsPreview?: Record<string, string | number | boolean>;
  ts: Timestamp | null;
}

/** CRM → agent work queues: Firestore request docs the Mac-mini workers poll. */
interface RequestQueueDef {
  collection: string;
  label: string;
  agentId: string;
  targetLabel: string;
  /** Firestore timestamp field used for ordering (default requestedAt). */
  timeField?: 'requestedAt' | 'createdAt';
  /** When set, only include docs matching this field value. */
  roleFilter?: string;
}

const REQUEST_QUEUES: RequestQueueDef[] = [
  {
    collection: 'travelAgentChatMessages',
    label: 'Chat message',
    agentId: 'calendarAgent',
    targetLabel: 'message',
    timeField: 'createdAt',
    roleFilter: 'user',
  },
  { collection: 'leadEnrichmentRequests', label: 'Lead enrichment', agentId: 'marketingAgent', targetLabel: 'lead' },
  { collection: 'leadBiddingAnalysisRequests', label: 'Bidding analysis', agentId: 'biddingAgent', targetLabel: 'lead' },
  { collection: 'proposalReviewRequests', label: 'Proposal review', agentId: 'biddingAgent', targetLabel: 'deal' },
  { collection: 'peopleTravelSearchRequests', label: 'Travel search', agentId: 'calendarAgent', targetLabel: 'trip' },
  { collection: 'peopleTravelCalendarSyncRequests', label: 'Calendar sync', agentId: 'calendarAgent', targetLabel: 'trip' },
  { collection: 'peopleTravelPlanRequests', label: 'Trip plan', agentId: 'calendarAgent', targetLabel: 'request' },
];

/** Logical agents whose registry docs live under their API-route id. */
const REGISTRY_DOC_ALIASES: Record<string, string> = {
  calendarAgent: 'travelAgent',
  filesAgent: 'biddingAgent',
};

/** Context domain for each CRM → agent queue (feed chips). */
const QUEUE_DOMAINS: Record<string, ActivityDomain> = {
  'Chat message': 'chat',
  'Lead enrichment': 'leads',
  'Bidding analysis': 'leads',
  'Proposal review': 'deals',
  'Travel search': 'trips',
  'Calendar sync': 'calendar',
  'Trip plan': 'trips',
};

/** One row in the humanized live feed (API event, queue request, or collapsed poll group). */
interface FeedItem {
  id: string;
  ts: Timestamp | null;
  agentId: string;
  sentence: string;
  domain: ActivityDomain;
  kind: ActivityKind | 'request';
  status: string;
  host?: string | null;
  detail?: string | null;
  rawAction?: string;
  source?: string;
  /** Raw params preview / queue meta — powers lens matching + canvas paths. */
  params?: Record<string, string | number | boolean> | null;
  /** > 1 when this row folds several routine checks together. */
  collapsedCount?: number;
}

interface QueueRequest {
  id: string;
  queue: RequestQueueDef;
  status: string;
  requestedAt: Timestamp | null;
  startedAt: Timestamp | null;
  completedAt: Timestamp | null;
  requestedBy?: string | null;
  error?: string | null;
  preview?: string | null;
  meta?: Record<string, unknown> | null;
}

interface DailyStat {
  id: string;
  agentId: string;
  date: string;
  calls: number;
  errors: number;
  denied: number;
  actions: Record<string, number>;
  lastEventAt: Timestamp | null;
}

interface MonthlyUsageAgent {
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  runs: number;
}

interface MonthlyUsage {
  month: string;
  totalCostUsd: number;
  agents: Record<string, MonthlyUsageAgent>;
}

interface CatalogAction {
  name: string;
  description?: string;
  params?: { name: string; type?: string; required?: boolean; description?: string }[];
  returns?: string;
}

interface CatalogApi {
  id: string;
  name: string;
  description: string;
  endpoint: string;
  actions: string[];
  guideJsonUrl: string;
}

interface DirectoryApi {
  id: string;
  name: string;
  description: string;
  endpoint: string;
  actions: CatalogAction[];
}

interface InstructionDoc {
  id: string;
  title: string;
  kind: string;
  description: string;
  jsonUrl: string;
}

/** Agent-reported CRM build / PR requests (new capabilities, API access, etc.). */
interface CrmBuildRequest {
  id: string;
  agentId: string;
  requestId: string;
  title: string;
  summary: string | null;
  kind: string;
  status: string;
  prUrl: string | null;
  prNumber: number | null;
  repo: string | null;
  host: string | null;
  author?: string | null;
  source?: 'firestore' | 'github';
  proposedAt: Timestamp | null;
  updatedAt: Timestamp | null;
}

const BUILD_KIND_LABELS: Record<string, string> = {
  new_capability: 'New capability',
  api_access: 'API access',
  schema_change: 'Schema / rules',
  integration: 'Integration',
  other: 'Other',
};

const BUILD_STATUS_LABELS: Record<string, string> = {
  proposed: 'Proposed',
  pr_open: 'PR open',
  pr_merged: 'Merged',
  pr_closed: 'PR closed',
  implemented: 'Live',
  declined: 'Declined',
};

const OPEN_BUILD_STATUSES = new Set(['proposed', 'pr_open']);

const BUILD_KIND_ASKS: Record<string, string> = {
  new_capability: 'needs a new CRM capability',
  api_access: 'needs API or gateway access',
  schema_change: 'needs a schema or Firestore rules change',
  integration: 'needs a new integration hook',
  other: 'is requesting a CRM change',
};

/** Plain-language description of what the agent is asking for. */
function buildRequestAsk(b: CrmBuildRequest): { text: string; fromAgent: boolean } {
  const summary = b.summary?.trim();
  if (summary) return { text: summary, fromAgent: true };
  const agent = b.agentId ? metaFor(b.agentId).label : 'An agent';
  const kindAsk = BUILD_KIND_ASKS[b.kind] || BUILD_KIND_ASKS.other;
  return {
    text: `${agent} ${kindAsk}: “${b.title}”.`,
    fromAgent: false,
  };
}

// ── Helpers ────────────────────────────────────────────────

function monthKey(d: Date): string {
  return d.toISOString().slice(0, 7);
}

function monthStartDate(d: Date): string {
  return `${monthKey(d)}-01`;
}

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

function fmtUsd(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
}

function relativeTime(ts: Timestamp | null): string {
  if (!ts) return '—';
  const ms = Date.now() - ts.toMillis();
  if (ms < 10_000) return 'just now';
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

function clockTime(ts: Timestamp | null): string {
  if (!ts) return '—';
  return ts.toDate().toLocaleTimeString('en-US', { hour12: false });
}

function localDocPath(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin === window.location.origin) return parsed.pathname;
    const hosted = parsed.pathname.match(/\/docs\/agents\/.+/);
    return hosted ? hosted[0] : url;
  } catch {
    return url.startsWith('/') ? url : `/docs/agents/${url}`;
  }
}

/** Resolve the logical agent id stored on activity events (handles legacy rows). */
function eventLogicalAgentId(e: ActivityEvent): string {
  if (e.agentId === 'travelAgent') return 'calendarAgent';
  return e.agentId || resolveDisplayAgentId(e.apiAgentId || '', e.action);
}

function sourceLabel(source: ActivityEvent['source']): string {
  switch (source) {
    case 'reported':
      return 'reported';
    case 'callable':
      return 'callable';
    case 'gateway':
      return 'gateway';
    case 'direct':
      return 'direct API';
    case 'chat':
      return 'chat';
    default:
      return source;
  }
}

// ── Page ───────────────────────────────────────────────────

type ActivityView = 'stream' | 'coordination' | 'directory';

const AgentActivityPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { crmUsers, leads, enhancedDeals } = useCRM();

  const designations = useMemo(() => {
    const me = crmUsers.find(u => u.uid === user?.id);
    return me?.designations ?? user?.designations;
  }, [crmUsers, user?.id, user?.designations]);
  const allowed = canViewAgentActivity({ authRole: user?.role, designations });

  const [view, setView] = useState<ActivityView>('stream');
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [stats, setStats] = useState<DailyStat[]>([]);
  const [usage, setUsage] = useState<MonthlyUsage | null>(null);
  const [agentFilter, setAgentFilter] = useState<string>('all');
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const [drillAgentId, setDrillAgentId] = useState<string | null>(null);
  const [queueRequests, setQueueRequests] = useState<Record<string, QueueRequest[]>>({});
  const [crmBuilds, setCrmBuilds] = useState<CrmBuildRequest[]>([]);
  const [githubBuilds, setGithubBuilds] = useState<CrmBuildRequest[]>([]);
  const [githubBuildsLoading, setGithubBuildsLoading] = useState(false);
  const [githubBuildsError, setGithubBuildsError] = useState<string | null>(null);
  const [capabilitySkills, setCapabilitySkills] = useState<Map<string, GbrainSkillRef[]>>(
    () => new Map(),
  );
  /** Only true on the first fetch — background refreshes must not flash the Skills column. */
  const [capabilitySkillsLoading, setCapabilitySkillsLoading] = useState(false);
  const [capabilitySkillsError, setCapabilitySkillsError] = useState<string | null>(null);
  const capabilitySkillsLoadedRef = useRef(false);
  const [buildStatusFilter, setBuildStatusFilter] = useState<string>('all');
  const [expandedBuildId, setExpandedBuildId] = useState<string | null>(null);
  const [directory, setDirectory] = useState<DirectoryApi[]>([]);
  const [directoryError, setDirectoryError] = useState<string | null>(null);
  const [directorySearch, setDirectorySearch] = useState('');
  const [instructionDocs, setInstructionDocs] = useState<InstructionDoc[]>([]);
  const [copiedInstructionId, setCopiedInstructionId] = useState<string | null>(null);
  const [expandedAction, setExpandedAction] = useState<string | null>(null);
  const [liveTail, setLiveTail] = useState(true);
  /** Universal lens — scope canvas, counts, and tickers to one entity or topic. */
  const [lens, setLens] = useState<FlowLens | null>(null);
  const [lensQuery, setLensQuery] = useState('');
  const [lensOpen, setLensOpen] = useState(false);
  /** Dropdown preview hits while typing. */
  const [brainHits, setBrainHits] = useState<CompanyBrainHit[]>([]);
  const [brainSearchLoading, setBrainSearchLoading] = useState(false);
  const [brainSearchError, setBrainSearchError] = useState<string | null>(null);
  const [brainSearchWarnings, setBrainSearchWarnings] = useState<string[]>([]);
  /** Below-map dossier: what we have / where it lives. */
  const [brainSubject, setBrainSubject] = useState<CompanyBrainSubject | null>(null);
  const [dossierHits, setDossierHits] = useState<CompanyBrainHit[]>([]);
  const [dossierBySection, setDossierBySection] = useState<Record<CompanyBrainSection, number> | null>(
    null,
  );
  const [dossierLoading, setDossierLoading] = useState(false);
  const [dossierError, setDossierError] = useState<string | null>(null);
  const [dossierWarnings, setDossierWarnings] = useState<string[]>([]);
  const [highlightCapabilityId, setHighlightCapabilityId] = useState<string | null>(null);
  /** Compact live-feed popup (toolbar button). */
  const [feedOpen, setFeedOpen] = useState(false);
  const [feedStatusFilter, setFeedStatusFilter] = useState<string>('all');
  const [eventsLoading, setEventsLoading] = useState(true);

  // ── Live stream subscription (global window for the canvas + feed popup) ──
  useEffect(() => {
    if (!allowed || !liveTail) {
      setEvents([]);
      setEventsLoading(false);
      return;
    }
    setEventsLoading(true);
    const q = query(
      collection(db, ACTIVITY_COLLECTION),
      orderBy('ts', 'desc'),
      limit(GLOBAL_FEED_LIMIT),
    );
    const unsub = onSnapshot(
      q,
      snap => {
        setEvents(
          snap.docs.map(d => {
            const data = d.data() as Omit<ActivityEvent, 'id'>;
            return { ...data, id: d.id };
          }),
        );
        setEventsLoading(false);
      },
      err => {
        console.error('[AgentActivity] stream error', err);
        setEventsLoading(false);
      },
    );
    return () => unsub();
  }, [allowed, liveTail]);

  useEffect(() => {
    if (!feedOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFeedOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [feedOpen]);

  // ── Month-to-date daily stats (all agents) ──
  useEffect(() => {
    if (!allowed) return;
    const q = query(
      collection(db, STATS_COLLECTION),
      where('date', '>=', monthStartDate(new Date())),
    );
    const unsub = onSnapshot(q, snap => {
      setStats(
        snap.docs.map(d => {
          const data = d.data() as Omit<DailyStat, 'id'>;
          return {
            id: d.id,
            agentId: data.agentId,
            date: data.date,
            calls: data.calls || 0,
            errors: data.errors || 0,
            denied: data.denied || 0,
            actions: data.actions || {},
            lastEventAt: data.lastEventAt || null,
          };
        }),
      );
    });
    return () => unsub();
  }, [allowed]);

  // ── Monthly usage (live — powers the MTD cost chip + drill-in) ──
  useEffect(() => {
    if (!allowed) return;
    const unsub = onSnapshot(doc(db, USAGE_MONTHLY_COLLECTION, monthKey(new Date())), snap => {
      setUsage(snap.exists() ? (snap.data() as MonthlyUsage) : null);
    });
    return () => unsub();
  }, [allowed]);

  // ── CRM build / PR requests (agent-reported) ──
  useEffect(() => {
    if (!allowed) return;
    const q = query(collection(db, CRM_BUILDS_COLLECTION), orderBy('updatedAt', 'desc'), limit(100));
    const unsub = onSnapshot(
      q,
      snap => {
        setCrmBuilds(
          snap.docs.map(d => {
            const data = d.data() as Record<string, any>;
            return {
              id: d.id,
              agentId: String(data.agentId || ''),
              requestId: String(data.requestId || d.id),
              title: String(data.title || 'Untitled'),
              summary: typeof data.summary === 'string' ? data.summary : null,
              kind: String(data.kind || 'other'),
              status: String(data.status || 'proposed'),
              prUrl: typeof data.prUrl === 'string' ? data.prUrl : null,
              prNumber: typeof data.prNumber === 'number' ? data.prNumber : null,
              repo: typeof data.repo === 'string' ? data.repo : null,
              host: typeof data.host === 'string' ? data.host : null,
              proposedAt: data.proposedAt instanceof Timestamp ? data.proposedAt : null,
              updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt : null,
            };
          }),
        );
      },
      err => console.warn('[AgentActivity] crm builds', err.message),
    );
    return () => unsub();
  }, [allowed]);

  // ── Historical GitHub PRs for CRM Builds ──
  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    (async () => {
      setGithubBuildsLoading(true);
      setGithubBuildsError(null);
      try {
        const fns = getFunctions(app, 'us-central1');
        const fn = httpsCallable<
          { limit?: number; state?: string },
          { pulls: Array<Record<string, unknown>>; errors?: Array<{ repo: string; status: number; message: string }> }
        >(fns, 'listAgentCrmPullRequests');
        const res = await fn({ limit: 100, state: 'all' });
        if (cancelled) return;
        const apiErrors = res.data?.errors || [];
        if (apiErrors.length) {
          setGithubBuildsError(
            apiErrors.map(e => `${e.repo}: ${e.status} ${e.message}`).join(' · '),
          );
        }
        const rows = (res.data?.pulls || []).map((p, idx) => {
          const proposed = typeof p.proposedAt === 'string' ? p.proposedAt : null;
          const updated = typeof p.updatedAt === 'string' ? p.updatedAt : proposed;
          const repo = typeof p.repo === 'string' ? p.repo : '';
          const repoKey = repo.replace(/[^\w.-]+/g, '-').replace(/^-+|-+$/g, '') || 'repo';
          return {
            id: `github-${repoKey}-${p.prNumber ?? idx}`,
            agentId: String(p.agentId || ''),
            requestId: String(p.prNumber ?? idx),
            title: String(p.title || 'Untitled PR'),
            summary: typeof p.summary === 'string' ? p.summary : null,
            kind: String(p.kind || 'other'),
            status: String(p.status || 'pr_open'),
            prUrl: typeof p.prUrl === 'string' ? p.prUrl : null,
            prNumber: typeof p.prNumber === 'number' ? p.prNumber : null,
            repo: typeof p.repo === 'string' ? p.repo : null,
            host: null,
            author: typeof p.author === 'string' ? p.author : null,
            source: 'github' as const,
            proposedAt: proposed ? Timestamp.fromDate(new Date(proposed)) : null,
            updatedAt: updated ? Timestamp.fromDate(new Date(updated)) : null,
          } satisfies CrmBuildRequest;
        });
        setGithubBuilds(rows);
      } catch (e) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : 'Failed to load GitHub PRs';
          setGithubBuildsError(msg);
          setGithubBuilds([]);
        }
      } finally {
        if (!cancelled) setGithubBuildsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [allowed]);

  // ── CompanyBrain skills (GitHub) for unified-agent Capabilities table ──
  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;

    const refresh = async (force = false) => {
      const showLoading = !capabilitySkillsLoadedRef.current;
      if (showLoading) setCapabilitySkillsLoading(true);
      setCapabilitySkillsError(null);
      try {
        const result = await listGbrainSkills(force);
        if (cancelled) return;
        const next = skillsMapFromResult(result);
        setCapabilitySkills(prev => {
          if (prev.size === next.size) {
            let same = true;
            for (const [id, skills] of Array.from(next.entries())) {
              const old = prev.get(id);
              if (!old || old.length !== skills.length) {
                same = false;
                break;
              }
              if (old.some((s, i) => s.path !== skills[i]?.path || s.name !== skills[i]?.name)) {
                same = false;
                break;
              }
            }
            if (same) return prev;
          }
          return next;
        });
        capabilitySkillsLoadedRef.current = true;
      } catch (e) {
        if (!cancelled) {
          setCapabilitySkillsError(e instanceof Error ? e.message : 'Failed to load skills');
        }
      } finally {
        if (!cancelled && showLoading) setCapabilitySkillsLoading(false);
      }
    };

    void refresh(false);
    const interval = window.setInterval(() => {
      void refresh(false);
    }, 2 * 60_000);

    const onFocus = () => {
      void refresh(false);
    };
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [allowed]);

  // ── CRM → agent request queues ──
  useEffect(() => {
    if (!allowed) return;
    const unsubs = REQUEST_QUEUES.map(queueDef => {
      const timeField = queueDef.timeField || 'requestedAt';
      return onSnapshot(
        query(collection(db, queueDef.collection), orderBy(timeField, 'desc'), limit(40)),
        snap => {
          setQueueRequests(prev => ({
            ...prev,
            [queueDef.collection]: snap.docs
              .map(d => {
                const data = d.data() as Record<string, any>;
                if (queueDef.roleFilter && data.role !== queueDef.roleFilter) return null;
                const ts = data[timeField];
                return {
                  id: d.id,
                  queue: queueDef,
                  status: String(data.status || 'pending'),
                  requestedAt: ts instanceof Timestamp ? ts : null,
                  startedAt: data.startedAt instanceof Timestamp ? data.startedAt : null,
                  completedAt: data.completedAt instanceof Timestamp ? data.completedAt : null,
                  requestedBy: typeof data.requestedBy === 'string' ? data.requestedBy : data.ownerUserId || null,
                  error: typeof data.error === 'string' ? data.error : null,
                  preview: typeof data.text === 'string' ? data.text.slice(0, 200) : null,
                  meta: data.meta && typeof data.meta === 'object' ? data.meta : null,
                } as QueueRequest;
              })
              .filter((row): row is QueueRequest => row !== null),
          }));
        },
        err => console.warn(`[AgentActivity] queue ${queueDef.collection}`, err.message),
      );
    });
    return () => unsubs.forEach(u => u());
  }, [allowed]);

  // ── Directory: catalog + per-API guides ──
  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(CATALOG_URL, { cache: 'no-cache' });
        if (!res.ok) throw new Error(`catalog ${res.status}`);
        const catalog = (await res.json()) as { apis: CatalogApi[] };
        const guides = await Promise.all(
          catalog.apis.map(async api => {
            try {
              const g = await fetch(localDocPath(api.guideJsonUrl), { cache: 'no-cache' });
              if (!g.ok) throw new Error(`${g.status}`);
              const guide = (await g.json()) as { actions?: CatalogAction[] };
              return {
                id: api.id,
                name: api.name,
                description: api.description,
                endpoint: api.endpoint,
                actions: guide.actions?.length
                  ? guide.actions
                  : api.actions.map(name => ({ name })),
              } as DirectoryApi;
            } catch {
              return {
                id: api.id,
                name: api.name,
                description: api.description,
                endpoint: api.endpoint,
                actions: api.actions.map(name => ({ name })),
              } as DirectoryApi;
            }
          }),
        );
        if (!cancelled) setDirectory(guides);

        try {
          const idxRes = await fetch(DOCS_INDEX_URL, { cache: 'no-cache' });
          if (idxRes.ok) {
            const idx = (await idxRes.json()) as { documentation?: InstructionDoc[] };
            const contracts = (idx.documentation || []).filter(d => d.kind === 'contract');
            if (!cancelled) setInstructionDocs(contracts);
          }
        } catch {
          /* catalog still usable without the instruction list */
        }
      } catch (e) {
        if (!cancelled) setDirectoryError((e as Error).message || 'Failed to load agent catalog');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [allowed]);

  // ── Derived aggregates ──

  /**
   * Self-reported usage keyed by logical agent — merges API-route ids
   * (travelAgent) into their display agent (calendarAgent) so cost lands
   * on the same card as the activity.
   */
  const usageAgents = useMemo(() => {
    const aliases: Record<string, string> = { travelAgent: 'calendarAgent' };
    const out: Record<string, MonthlyUsageAgent> = {};
    for (const [id, u] of Object.entries(usage?.agents || {})) {
      const key = aliases[id] || id;
      const prev = out[key];
      out[key] = prev
        ? {
            costUsd: (prev.costUsd || 0) + (u.costUsd || 0),
            inputTokens: (prev.inputTokens || 0) + (u.inputTokens || 0),
            outputTokens: (prev.outputTokens || 0) + (u.outputTokens || 0),
            runs: (prev.runs || 0) + (u.runs || 0),
          }
        : { ...u };
    }
    return out;
  }, [usage]);

  /** Per-agent rollup for the rail — stats docs are already one row per logical agent per day. */
  const agentSummaries = useMemo(() => {
    const byAgent = new Map<
      string,
      {
        agentId: string;
        callsToday: number;
        callsMonth: number;
        errorsMonth: number;
        lastEventAt: Timestamp | null;
        daily: Map<string, number>;
      }
    >();
    const today = todayKey();

    const ensure = (agentId: string) => {
      let entry = byAgent.get(agentId);
      if (!entry) {
        entry = {
          agentId,
          callsToday: 0,
          callsMonth: 0,
          errorsMonth: 0,
          lastEventAt: null,
          daily: new Map(),
        };
        byAgent.set(agentId, entry);
      }
      return entry;
    };

    for (const s of stats) {
      const agentId = s.agentId === 'travelAgent' ? 'calendarAgent' : s.agentId;
      if (!agentId) continue;
      const entry = ensure(agentId);
      const dayCalls =
        typeof s.calls === 'number' && s.calls > 0
          ? s.calls
          : Object.values(s.actions || {}).reduce((sum, n) => sum + n, 0);
      entry.callsMonth += dayCalls;
      if (s.date === today) entry.callsToday += dayCalls;
      entry.errorsMonth += (s.errors || 0) + (s.denied || 0);
      if (s.date && dayCalls > 0) {
        entry.daily.set(s.date, (entry.daily.get(s.date) || 0) + dayCalls);
      }
      if (s.lastEventAt && (!entry.lastEventAt || s.lastEventAt.toMillis() > entry.lastEventAt.toMillis())) {
        entry.lastEventAt = s.lastEventAt;
      }
    }

    for (const agentId of Object.keys(usageAgents)) {
      ensure(agentId);
    }

    return Array.from(byAgent.values())
      .filter(a => a.agentId !== 'travelAgent')
      .sort((a, b) => b.callsMonth - a.callsMonth);
  }, [stats, usageAgents]);

  /** Month action counts per logical agent for the directory + drill-in. */
  const actionCounts = useMemo(() => {
    const byAgent = new Map<string, Record<string, number>>();
    for (const s of stats) {
      for (const [action, count] of Object.entries(s.actions || {})) {
        const id = resolveDisplayAgentId(s.agentId, action);
        const entry = byAgent.get(id) || {};
        entry[action] = (entry[action] || 0) + count;
        byAgent.set(id, entry);
      }
    }
    return byAgent;
  }, [stats]);

  /** All queue rows merged, newest first. */
  const allRequests = useMemo(() => {
    const rows = Object.values(queueRequests).flat();
    rows.sort((a, b) => (b.requestedAt?.toMillis() || 0) - (a.requestedAt?.toMillis() || 0));
    return rows;
  }, [queueRequests]);

  /** uid → first name for "with Sarah" attributions. */
  const employeeNameByUid = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of crmUsers) {
      const first = (u.displayName || '').trim().split(/\s+/)[0];
      if (u.uid && first) map.set(u.uid, first);
    }
    return map;
  }, [crmUsers]);

  /** Every feed-able moment, newest first, before poll-collapsing. */
  const rawFeedItems = useMemo(() => {
    const items: FeedItem[] = [];

    for (const e of events) {
      const h = humanizeEvent(e.action, e.paramsPreview, e.source);
      items.push({
        id: `ev-${e.id}`,
        ts: e.ts,
        agentId: eventLogicalAgentId(e),
        sentence: h.sentence,
        domain: h.domain,
        kind: h.kind,
        status: e.status,
        host: e.host,
        rawAction: e.action,
        source: e.source,
        params: e.paramsPreview || null,
      });
    }

    for (const r of allRequests) {
      const name = r.requestedBy ? employeeNameByUid.get(r.requestedBy) || null : null;
      const metaParams: Record<string, string | number | boolean> = {};
      for (const [k, v] of Object.entries(r.meta || {})) {
        if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
          metaParams[k] = v;
        }
      }
      items.push({
        id: `rq-${r.queue.collection}-${r.id}`,
        ts: r.requestedAt,
        agentId: r.queue.agentId,
        sentence: humanizeQueueRequest(r.queue.label, name),
        domain: QUEUE_DOMAINS[r.queue.label] || 'system',
        kind: 'request',
        status: r.status,
        detail: r.preview ? r.preview.slice(0, 100) : null,
        rawAction: `request${r.queue.label.replace(/\s+/g, '')}`,
        params: Object.keys(metaParams).length ? metaParams : null,
      });
    }

    items.sort((a, b) => (b.ts?.toMillis() || 0) - (a.ts?.toMillis() || 0));
    return items;
  }, [events, allRequests, employeeNameByUid]);

  /** Feed rows with consecutive routine checks folded into one muted line. */
  const feedRows = useMemo(() => {
    let rows = rawFeedItems;
    if (lens) rows = rows.filter(i => itemMatchesLens(i as FlowFeedItem, lens));
    if (feedStatusFilter !== 'all') {
      rows = rows.filter(i =>
        feedStatusFilter === 'error'
          ? i.status === 'error' || i.status === 'failed' || i.status === 'denied'
          : i.status === feedStatusFilter,
      );
    }

    const out: FeedItem[] = [];
    for (const item of rows) {
      const isPoll = item.rawAction ? isPollEvent(item.rawAction, item.source) : false;
      const prev = out[out.length - 1];
      if (
        isPoll &&
        prev &&
        prev.collapsedCount !== undefined &&
        prev.agentId === item.agentId
      ) {
        prev.collapsedCount += 1;
        continue;
      }
      if (isPoll) {
        out.push({
          ...item,
          sentence: 'routine checks — polled queues / reported usage',
          collapsedCount: 1,
        });
        continue;
      }
      out.push(item);
    }
    return out.slice(0, 80);
  }, [rawFeedItems, feedStatusFilter, lens]);

  /** "Right now" board — one row per agent with state, last action, collaborator. */
  const boardRows = useMemo(() => {
    const latestByAgent = new Map<string, FeedItem>();
    const latestMeaningfulByAgent = new Map<string, FeedItem>();
    for (const item of rawFeedItems) {
      if (!latestByAgent.has(item.agentId)) latestByAgent.set(item.agentId, item);
      const isPoll = item.rawAction ? isPollEvent(item.rawAction, item.source) : false;
      if (!isPoll && !latestMeaningfulByAgent.has(item.agentId)) {
        latestMeaningfulByAgent.set(item.agentId, item);
      }
    }

    const openByAgent = new Map<string, QueueRequest>();
    for (const r of allRequests) {
      if (r.status !== 'pending' && r.status !== 'processing') continue;
      if (!openByAgent.has(r.queue.agentId)) openByAgent.set(r.queue.agentId, r);
    }

    return agentSummaries.map(a => {
      const meta = metaFor(a.agentId);
      const latest = latestMeaningfulByAgent.get(a.agentId) || latestByAgent.get(a.agentId) || null;
      const openReq = openByAgent.get(a.agentId) || null;
      const lastMs = Math.max(
        a.lastEventAt?.toMillis() || 0,
        latest?.ts?.toMillis() || 0,
      );
      const working = (lastMs > 0 && Date.now() - lastMs < 5 * 60_000) || openReq?.status === 'processing';
      const withName = openReq?.requestedBy ? employeeNameByUid.get(openReq.requestedBy) || null : null;
      return {
        agentId: a.agentId,
        meta,
        working,
        lastMs,
        lastSentence: latest?.sentence || null,
        lastTs: latest?.ts || a.lastEventAt,
        withName,
        openQueueLabel: openReq ? openReq.queue.label.toLowerCase() : null,
      };
    });
  }, [agentSummaries, rawFeedItems, allRequests, employeeNameByUid]);

  /**
   * Per-skill live state for the unified-agent card.
   * Prefers explicit skillsUsed on each event; falls back to agent→skill fold.
   */
  const skillStatesRaw = useMemo(() => {
    const map = new Map<string, FlowSkillState>();
    const withByAgent = new Map(boardRows.map(r => [r.agentId, r.withName] as const));
    for (const item of rawFeedItems) {
      const poll = item.rawAction ? isPollEvent(item.rawAction, item.source) : false;
      if (poll) continue;
      const explicit = reportedSkillIds(item.params);
      const skillIds = explicit.length
        ? explicit
        : ([resolveSkillId(item.agentId, item.params)].filter(Boolean) as string[]);
      if (!skillIds.length) continue;
      const lastMs = item.ts?.toMillis() || 0;
      const working = lastMs > 0 && Date.now() - lastMs < 5 * 60_000;
      for (const skillId of skillIds) {
        const prev = map.get(skillId);
        if (!prev || lastMs > (prev.lastTs?.toMillis() || 0)) {
          map.set(skillId, {
            working: working || prev?.working || false,
            lastSentence: item.sentence,
            lastTs: item.ts,
            withName: withByAgent.get(item.agentId) || null,
          });
        } else if (working && !prev.working) {
          map.set(skillId, { ...prev, working: true });
        }
      }
    }
    return map;
  }, [rawFeedItems, boardRows]);

  /** Keep Map identity stable across poll-only snapshot churn. */
  const skillStatesRef = useRef(skillStatesRaw);
  if (!skillStatesMapsEqual(skillStatesRef.current, skillStatesRaw)) {
    skillStatesRef.current = skillStatesRaw;
  }
  const skillStates = skillStatesRef.current;

  /**
   * Canvas feed: drop routine polls and hold a stable array reference until a
   * meaningful event actually changes (prevents React Flow remount flash).
   */
  const flowItemsRaw = useMemo(
    () =>
      rawFeedItems.filter(item => {
        if (!item.rawAction) return true;
        return !isPollEvent(item.rawAction, item.source);
      }) as FlowFeedItem[],
    [rawFeedItems],
  );
  const flowItemsRef = useRef(flowItemsRaw);
  if (!flowItemsSignatureEqual(flowItemsRef.current, flowItemsRaw)) {
    flowItemsRef.current = flowItemsRaw;
  }
  const flowItems = flowItemsRef.current;

  /** Recent meaningful actions that omitted skillsUsed — shown as a Stream warning. */
  const missingSkillCount = useMemo(() => {
    let n = 0;
    for (const item of rawFeedItems) {
      if (!item.rawAction) continue;
      const poll = isPollEvent(item.rawAction, item.source);
      if (isMissingSkill(item.rawAction, item.params, poll, item.source)) n += 1;
    }
    return n;
  }, [rawFeedItems]);

  const closeDrill = useCallback(() => {
    setDrillAgentId(null);
  }, []);

  /** Universal lens suggestions — deals, leads, people from loaded CRM context + topic fallback. */
  const lensSuggestions = useMemo(() => {
    const needle = lensQuery.trim().toLowerCase();
    if (needle.length < 2) return [];
    const out: Array<{ key: string; label: string; sub: string; lens: FlowLens }> = [];

    for (const d of enhancedDeals) {
      const name = (d as any).projectName || d.contactName || '';
      const num = (d as any).projectNumber || '';
      if (!d.id || !name) continue;
      if (`${name} ${num} ${d.company || ''}`.toLowerCase().includes(needle)) {
        out.push({
          key: `deal-${d.id}`,
          label: name,
          sub: `Deal${num ? ` · ${num}` : ''}`,
          lens: { mode: 'entity', entityType: 'deal', id: d.id, label: name },
        });
        if (out.length >= 5) break;
      }
    }

    let leadCount = 0;
    for (const l of leads) {
      if (!l.id) continue;
      const name = `${l.firstName || ''} ${l.lastName || ''}`.trim();
      if (!name) continue;
      if (`${name} ${l.company || ''} ${l.location || ''}`.toLowerCase().includes(needle)) {
        out.push({
          key: `lead-${l.id}`,
          label: name,
          sub: `Lead${l.company ? ` · ${l.company}` : ''}`,
          lens: { mode: 'entity', entityType: 'lead', id: l.id, label: name },
        });
        leadCount += 1;
        if (leadCount >= 5) break;
      }
    }

    let personCount = 0;
    for (const u of crmUsers) {
      const name = (u.displayName || '').trim();
      if (!u.uid || !name) continue;
      if (name.toLowerCase().includes(needle)) {
        out.push({
          key: `person-${u.uid}`,
          label: name,
          sub: 'Teammate',
          lens: { mode: 'entity', entityType: 'person', id: u.uid, label: name },
        });
        personCount += 1;
        if (personCount >= 3) break;
      }
    }

    return out;
  }, [lensQuery, enhancedDeals, leads, crmUsers]);

  const clearDossier = useCallback(() => {
    setBrainSubject(null);
    setDossierHits([]);
    setDossierBySection(null);
    setDossierError(null);
    setDossierWarnings([]);
    setDossierLoading(false);
    setHighlightCapabilityId(null);
  }, []);

  const loadDossier = useCallback(
    async (input: {
      query: string;
      subjectId?: string | null;
      subjectType?: 'lead' | 'deal' | 'topic';
      subjectLabel?: string;
    }) => {
      setDossierLoading(true);
      setDossierError(null);
      try {
        const res = await searchCompanyBrain({
          query: input.query,
          subjectId: input.subjectId,
          subjectType: input.subjectType,
          subjectLabel: input.subjectLabel,
        });
        setBrainSubject(
          res.subject ||
            (input.subjectLabel
              ? {
                  type: input.subjectType || 'topic',
                  id: input.subjectId || null,
                  label: input.subjectLabel,
                }
              : null),
        );
        setDossierHits(res.hits || []);
        setDossierBySection(res.bySection || null);
        setDossierWarnings(res.warnings || []);
        const capHit = (res.hits || []).find(h => h.capabilityId);
        if (capHit?.capabilityId) setHighlightCapabilityId(capHit.capabilityId);
      } catch (e) {
        setDossierHits([]);
        setDossierBySection(null);
        setDossierError(e instanceof Error ? e.message : 'Brain search failed');
      } finally {
        setDossierLoading(false);
      }
    },
    [],
  );

  const applyLens = useCallback((next: FlowLens | null) => {
    setLens(next);
    setLensQuery('');
    setLensOpen(false);
  }, []);

  /** Pick a CRM subject: focus map + fill below-map dossier. */
  const openSubject = useCallback(
    (next: FlowLens) => {
      applyLens(next);
      if (next.mode === 'entity') {
        // Person lenses aren't CRM lead/deal subjects — search as a topic by name.
        if (next.entityType === 'person') {
          void loadDossier({
            query: next.label,
            subjectType: 'topic',
            subjectLabel: next.label,
          });
        } else {
          void loadDossier({
            query: next.label,
            subjectId: next.id,
            subjectType: next.entityType,
            subjectLabel: next.label,
          });
        }
      } else {
        void loadDossier({
          query: next.text,
          subjectType: 'topic',
          subjectLabel: next.text,
        });
      }
    },
    [applyLens, loadDossier],
  );

  /** Asset preview → resolve subject dossier (entity if known). */
  const openBrainHit = useCallback(
    (hit: CompanyBrainHit) => {
      setLensOpen(false);
      setLensQuery('');
      if (hit.capabilityId) setHighlightCapabilityId(hit.capabilityId);
      if (hit.entityRef?.type === 'deal' || hit.entityRef?.type === 'lead') {
        openSubject({
          mode: 'entity',
          entityType: hit.entityRef.type,
          id: hit.entityRef.id,
          label: hit.entityRef.label || hit.title,
        });
        return;
      }
      const topic = (hit.path || hit.title).split('/').pop() || hit.title;
      void loadDossier({
        query: hit.title,
        subjectType: 'topic',
        subjectLabel: topic.replace(/\.md$/i, ''),
      });
    },
    [loadDossier, openSubject],
  );

  const focusMapFromDossier = useCallback(() => {
    if (!brainSubject) return;
    if (brainSubject.type === 'lead' || brainSubject.type === 'deal') {
      if (!brainSubject.id) return;
      applyLens({
        mode: 'entity',
        entityType: brainSubject.type,
        id: brainSubject.id,
        label: brainSubject.label,
      });
      return;
    }
    applyLens({ mode: 'topic', text: brainSubject.label });
  }, [applyLens, brainSubject]);

  // Debounced Company Brain search (dropdown preview only)
  useEffect(() => {
    const q = lensQuery.trim();
    if (q.length < 2) {
      setBrainHits([]);
      setBrainSearchError(null);
      setBrainSearchWarnings([]);
      setBrainSearchLoading(false);
      return;
    }
    let cancelled = false;
    setBrainSearchLoading(true);
    const t = window.setTimeout(() => {
      void (async () => {
        try {
          const res = await searchCompanyBrain({ query: q });
          if (cancelled) return;
          setBrainHits(res.hits || []);
          setBrainSearchWarnings(res.warnings || []);
          setBrainSearchError(null);
        } catch (e) {
          if (cancelled) return;
          setBrainHits([]);
          setBrainSearchError(e instanceof Error ? e.message : 'Brain search failed');
        } finally {
          if (!cancelled) setBrainSearchLoading(false);
        }
      })();
    }, 320);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [lensQuery]);

  const dossierSections = useMemo(() => groupHitsBySection(dossierHits), [dossierHits]);
  const dossierMeetingCount = useMemo(
    () => dossierHits.filter(h => h.kind === 'meeting').length,
    [dossierHits],
  );
  const dossierDocCount = useMemo(
    () => dossierHits.filter(h => h.section === 'github' || h.kind === 'marketing').length,
    [dossierHits],
  );
  const dossierActivityCount = useMemo(
    () => dossierHits.filter(h => h.section === 'activity').length,
    [dossierHits],
  );

  /** Stable merge key — PR numbers collide across repos (e.g. CEO_Agent #7 vs lctf_clients #7). */
  const buildMergeKey = useCallback((b: CrmBuildRequest): string => {
    if (b.prUrl) return `url-${b.prUrl}`;
    const repo = (b.repo || '').trim().toLowerCase();
    if (b.prNumber != null && repo) return `pr-${repo}-${b.prNumber}`;
    if (b.prNumber != null) return `pr-${b.prNumber}`;
    return b.source === 'firestore' ? `fs-${b.id}` : b.id;
  }, []);

  /** GitHub PR history merged with agent-reported Firestore rows (agent ids folded to logical agents). */
  const mergedBuilds = useMemo(() => {
    const fold = (b: CrmBuildRequest): CrmBuildRequest =>
      b.agentId ? { ...b, agentId: resolveDisplayAgentId(b.agentId, '') } : b;
    const map = new Map<string, CrmBuildRequest>();
    for (const g of githubBuilds) {
      map.set(buildMergeKey(g), fold(g));
    }
    for (const f of crmBuilds) {
      const key = buildMergeKey(f);
      const prev = map.get(key);
      const row = fold(f);
      map.set(key, prev ? { ...prev, ...row, source: 'firestore' } : { ...row, source: 'firestore' });
    }
    return Array.from(map.values()).sort(
      (a, b) => (b.updatedAt?.toMillis() || 0) - (a.updatedAt?.toMillis() || 0),
    );
  }, [crmBuilds, githubBuilds, buildMergeKey]);

  const mtdCost = usage?.totalCostUsd || 0;
  const drillAgent = drillAgentId ? agentSummaries.find(a => a.agentId === drillAgentId) : null;

  /** Raw API events for the drill-in (Directory / power-user escape hatch). */
  const drillEvents = useMemo(() => {
    if (!drillAgentId) return [];
    return events.filter(e => eventLogicalAgentId(e) === drillAgentId).slice(0, 50);
  }, [events, drillAgentId]);

  const openRequestCount = useMemo(
    () => allRequests.filter(r => r.status === 'pending' || r.status === 'processing').length,
    [allRequests],
  );

  const openBuildCount = useMemo(
    () => mergedBuilds.filter(b => OPEN_BUILD_STATUSES.has(b.status)).length,
    [mergedBuilds],
  );

  const filteredBuilds = useMemo(() => {
    let rows = mergedBuilds;
    if (agentFilter !== 'all') rows = rows.filter(b => b.agentId === agentFilter);
    if (buildStatusFilter === 'open') {
      rows = rows.filter(b => OPEN_BUILD_STATUSES.has(b.status));
    } else if (buildStatusFilter !== 'all') {
      rows = rows.filter(b => b.status === buildStatusFilter);
    }
    return rows;
  }, [mergedBuilds, agentFilter, buildStatusFilter]);

  const filteredDirectory = useMemo(() => {
    const needle = directorySearch.trim().toLowerCase();
    if (!needle) return directory;
    return directory
      .map(api => {
        const apiMatches =
          api.name.toLowerCase().includes(needle) || api.description.toLowerCase().includes(needle);
        const actions = api.actions.filter(
          a =>
            apiMatches ||
            a.name.toLowerCase().includes(needle) ||
            (a.description || '').toLowerCase().includes(needle),
        );
        return { ...api, actions };
      })
      .filter(api => api.actions.length > 0);
  }, [directory, directorySearch]);

  if (!user) return null;
  if (!allowed) return <Navigate to="/" replace />;

  return (
    <div className="ops-hub aa-page">
      <AppHeader />
      <div className="aa-main">
        {/* ── Toolbar ── */}
        <div className="aa-toolbar">
          <div className="aa-toolbar-left">
            <h1 className="aa-title">Agent Activity</h1>
            <span className="aa-subtitle">
              Live path of context into gbrain, through the agent, and back to people
            </span>
          </div>
          <div className="aa-toolbar-right">
            <div className="aa-view-toggle" role="tablist" aria-label="Agent activity view">
              <button
                type="button"
                role="tab"
                aria-selected={view === 'stream'}
                className={`aa-view-btn${view === 'stream' ? ' active' : ''}`}
                onClick={() => setView('stream')}
              >
                <Radio size={14} />
                <span>Stream</span>
                {openRequestCount > 0 && <span className="aa-tab-badge">{openRequestCount}</span>}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={view === 'coordination'}
                className={`aa-view-btn${view === 'coordination' ? ' active' : ''}`}
                onClick={() => setView('coordination')}
              >
                <GitPullRequest size={14} />
                <span>Coordination</span>
                {openBuildCount > 0 && <span className="aa-tab-badge">{openBuildCount}</span>}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={view === 'directory'}
                className={`aa-view-btn${view === 'directory' ? ' active' : ''}`}
                onClick={() => setView('directory')}
              >
                <LayoutList size={14} />
                <span>Directory</span>
              </button>
            </div>

            <div className="aa-feed-popover-wrap">
              <button
                type="button"
                className={`aa-feed-btn${feedOpen ? ' open' : ''}`}
                onClick={() => setFeedOpen(v => !v)}
                aria-expanded={feedOpen}
                aria-haspopup="dialog"
                title="Open live feed"
              >
                <Activity size={14} />
                <span>Feed</span>
                {!eventsLoading && feedRows.length > 0 && (
                  <span className="aa-tab-badge">{Math.min(feedRows.length, 99)}</span>
                )}
              </button>
              {feedOpen && (
                <>
                  <button
                    type="button"
                    className="aa-feed-popover-backdrop"
                    aria-label="Close live feed"
                    onClick={() => setFeedOpen(false)}
                  />
                  <aside className="aa-feed aa-feed-popup" role="dialog" aria-label="Live feed">
                    <div className="aa-feed-head">
                      <h3 className="aa-stream-panel-title">
                        <Radio size={15} />
                        Live feed
                        {lens && (
                          <span className="aa-stream-agent-filter">
                            · {lens.mode === 'entity' ? lens.label : `“${lens.text}”`}
                          </span>
                        )}
                        <button
                          type="button"
                          className="aa-feed-clear"
                          onClick={() => setFeedOpen(false)}
                          title="Close"
                        >
                          <X size={12} />
                        </button>
                      </h3>
                      <div className="aa-stream-filters aa-stream-panel-filters">
                        <select
                          className="aa-select"
                          value={feedStatusFilter}
                          onChange={e => setFeedStatusFilter(e.target.value)}
                          aria-label="Filter feed by status"
                        >
                          <option value="all">Everything</option>
                          <option value="ok">Successful only</option>
                          <option value="error">Issues only</option>
                        </select>
                        <span className="aa-stream-count">
                          {feedRows.length} moment{feedRows.length === 1 ? '' : 's'}
                          {openRequestCount > 0
                            ? ` · ${openRequestCount} open request${openRequestCount === 1 ? '' : 's'}`
                            : ''}
                        </span>
                      </div>
                    </div>

                    {eventsLoading && (
                      <div className="aa-stream-loading">Connecting to live feed…</div>
                    )}
                    {!eventsLoading && feedRows.length === 0 && (
                      <div className="aa-stream-empty aa-stream-empty-compact">
                        <Radio size={18} />
                        <p>
                          Nothing here yet. Agent work appears the moment it happens — CRM API
                          calls, queued requests from the team, and work reported from Slack or
                          the Mac mini.
                        </p>
                      </div>
                    )}

                    <div className="aa-feed-rows">
                      {feedRows.map(item => {
                        const meta = metaFor(item.agentId);
                        const isIssue =
                          item.status === 'error' ||
                          item.status === 'failed' ||
                          item.status === 'denied';
                        const collapsed = item.collapsedCount !== undefined;
                        const isRequest = item.kind === 'request';
                        return (
                          <div
                            key={item.id}
                            className={`aa-feed-row${collapsed ? ' muted' : ''}${isIssue ? ' issue' : ''}`}
                            title={
                              item.rawAction
                                ? `${item.rawAction} · ${sourceLabel((item.source || 'direct') as ActivityEvent['source'])}`
                                : undefined
                            }
                          >
                            <span
                              className="aa-event-time"
                              title={item.ts ? item.ts.toDate().toLocaleString() : ''}
                            >
                              {relativeTime(item.ts)}
                            </span>
                            <span
                              className="aa-event-agent"
                              style={{ ['--aa-accent' as any]: meta.accent }}
                            >
                              {meta.icon}
                              <span>{meta.label}</span>
                            </span>
                            <span
                              className={`aa-feed-kind aa-kind-${isRequest ? 'request' : item.kind}`}
                              title={
                                isRequest
                                  ? 'request from the team'
                                  : `${item.kind} — context ${item.kind === 'write' ? 'written' : 'accessed'}`
                              }
                            />
                            <span className="aa-feed-sentence">
                              {item.sentence}
                              {collapsed && (item.collapsedCount || 0) > 1 && (
                                <span className="aa-feed-count"> ×{item.collapsedCount}</span>
                              )}
                              {item.detail && (
                                <span className="aa-feed-detail"> — “{item.detail}”</span>
                              )}
                            </span>
                            <span className="aa-chip aa-chip-domain">
                              {DOMAIN_LABELS[item.domain]}
                            </span>
                            {isRequest && !isIssue && (
                              <span className={`aa-event-status aa-rstatus-${item.status}`}>
                                {item.status}
                              </span>
                            )}
                            {isIssue && (
                              <span className="aa-event-status aa-status-error">{item.status}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </aside>
                </>
              )}
            </div>

            <span className="aa-cost-chip" title="Month-to-date agent cost (self-reported)">
              <DollarSign size={13} />
              <span>{fmtUsd(mtdCost)} MTD</span>
            </span>
            <button
              type="button"
              className={`aa-live-btn${liveTail ? ' live' : ''}`}
              onClick={() => setLiveTail(v => !v)}
              title={liveTail ? 'Live tail on — click to pause' : 'Live tail paused — click to resume'}
            >
              <span className="aa-live-dot" />
              <span>{liveTail ? 'Live' : 'Paused'}</span>
            </button>
          </div>
        </div>

        {view === 'stream' && (
          <div className="aa-content aa-content-flow">
            {/* ── Brain-flow canvas hero ── */}
            <section className="aa-flow-hero">
              <div className="aa-lens-bar">
                <div className="aa-lens-input-wrap">
                  <Search size={14} />
                  <input
                    type="text"
                    className="aa-lens-input"
                    placeholder="Search Company Brain — deals, skills, briefings, notes…"
                    value={lensQuery}
                    onChange={e => {
                      setLensQuery(e.target.value);
                      setLensOpen(true);
                    }}
                    onFocus={() => setLensOpen(true)}
                    onBlur={() => setTimeout(() => setLensOpen(false), 160)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && lensQuery.trim()) {
                        if (lensSuggestions[0]) openSubject(lensSuggestions[0].lens);
                        else if (brainHits[0]) openBrainHit(brainHits[0]);
                        else openSubject({ mode: 'topic', text: lensQuery.trim() });
                      }
                      if (e.key === 'Escape') {
                        setLensOpen(false);
                      }
                    }}
                    aria-label="Search Company Brain"
                  />
                  {lensOpen && lensQuery.trim().length >= 2 && (
                    <div className="aa-lens-suggestions aa-brain-suggestions" role="listbox">
                      <div className="aa-suggest-group-label">Subjects</div>
                      {lensSuggestions.map(s => (
                        <button
                          key={s.key}
                          type="button"
                          className="aa-lens-suggestion"
                          onMouseDown={e => {
                            e.preventDefault();
                            openSubject(s.lens);
                          }}
                        >
                          <span className="aa-lens-suggestion-label">{s.label}</span>
                          <span className="aa-lens-suggestion-sub">{s.sub}</span>
                        </button>
                      ))}
                      <button
                        type="button"
                        className="aa-lens-suggestion aa-lens-suggestion-topic"
                        onMouseDown={e => {
                          e.preventDefault();
                          openSubject({ mode: 'topic', text: lensQuery.trim() });
                        }}
                      >
                        <span className="aa-lens-suggestion-label">
                          On “{lensQuery.trim()}”
                        </span>
                        <span className="aa-lens-suggestion-sub">Topic dossier + map focus</span>
                      </button>

                      <div className="aa-suggest-group-label">Assets</div>
                      {brainSearchLoading && (
                        <div className="aa-suggest-empty">Searching brain…</div>
                      )}
                      {!brainSearchLoading && brainSearchError && (
                        <div className="aa-suggest-empty">{brainSearchError}</div>
                      )}
                      {!brainSearchLoading && !brainSearchError && brainHits.length === 0 && (
                        <div className="aa-suggest-empty">
                          No asset hits yet — try a skill, deal, or briefing name.
                        </div>
                      )}
                      {!brainSearchLoading &&
                        brainHits.slice(0, 6).map(hit => (
                          <button
                            key={hit.id}
                            type="button"
                            className="aa-lens-suggestion aa-brain-hit"
                            onMouseDown={e => {
                              e.preventDefault();
                              openBrainHit(hit);
                            }}
                          >
                            <span className="aa-brain-hit-main">
                              <span className="aa-lens-suggestion-label">{hit.title}</span>
                              <span className="aa-brain-hit-location">
                                {hit.location || hit.path || '—'}
                              </span>
                            </span>
                            <span className="aa-lens-suggestion-sub">
                              {brainKindLabel(hit.kind)}
                            </span>
                          </button>
                        ))}
                      {brainSearchWarnings.length > 0 && (
                        <div className="aa-suggest-warn">{brainSearchWarnings[0]}</div>
                      )}
                    </div>
                  )}
                </div>
                {lens && (
                  <span className="aa-lens-chip">
                    <span>{lens.mode === 'entity' ? lens.label : `“${lens.text}”`}</span>
                    <button
                      type="button"
                      className="aa-lens-clear"
                      onClick={() => applyLens(null)}
                      title="Clear map focus"
                    >
                      <X size={11} />
                    </button>
                  </span>
                )}
                {brainSubject && (
                  <span className="aa-lens-chip aa-brain-chip">
                    <span>On {brainSubject.label}</span>
                    <button
                      type="button"
                      className="aa-lens-clear"
                      onClick={clearDossier}
                      title="Clear dossier"
                    >
                      <X size={11} />
                    </button>
                  </span>
                )}
                <span
                  className="aa-lens-hint"
                  title="Searches CompanyBrain on GitHub, CRM digests, and recent agent retrievals. Results fill the strip below the map."
                >
                  what / where
                </span>
                {openRequestCount > 0 && (
                  <span className="aa-lens-hint" title="Open CRM → agent queue requests">
                    {openRequestCount} open request{openRequestCount === 1 ? '' : 's'}
                  </span>
                )}
                {missingSkillCount > 0 && (
                  <span
                    className="aa-skill-missing-chip"
                    title="Agent actions should include skillsUsed so the unified agent can route and the canvas can light the right skill"
                  >
                    <AlertTriangle size={12} />
                    {missingSkillCount} missing skill
                    {missingSkillCount === 1 ? '' : 's'}
                  </span>
                )}
              </div>
              <div className="aa-flow-stack">
                <BrainContextFlow
                  items={flowItems}
                  skillStates={skillStates}
                  lens={lens}
                  capabilitySkills={capabilitySkills}
                  capabilitySkillsLoading={capabilitySkillsLoading}
                  capabilitySkillsError={capabilitySkillsError}
                  highlightCapabilityId={highlightCapabilityId}
                />
                {(brainSubject || dossierLoading || dossierError) && (
                  <section className="aa-brain-dossier" aria-label="Company Brain dossier">
                    <div className="aa-brain-dossier-head">
                      <div className="aa-brain-dossier-title-block">
                        <h3 className="aa-brain-dossier-title">
                          On {brainSubject?.label || '…'}
                        </h3>
                        <div className="aa-brain-dossier-counts">
                          {dossierLoading && <span>loading…</span>}
                          {!dossierLoading && !dossierError && (
                            <>
                              <span>{dossierMeetingCount} meetings</span>
                              <span>{dossierDocCount} docs</span>
                              <span>{dossierActivityCount} retrievals</span>
                              {dossierBySection && (
                                <span>
                                  {(dossierBySection.crm || 0) + (dossierBySection.github || 0)}{' '}
                                  assets
                                </span>
                              )}
                            </>
                          )}
                          {dossierError && <span className="aa-brain-dossier-error">{dossierError}</span>}
                        </div>
                      </div>
                      <div className="aa-brain-dossier-actions">
                        {brainSubject && (
                          <button
                            type="button"
                            className="aa-brain-focus-btn"
                            onClick={focusMapFromDossier}
                          >
                            Focus map
                          </button>
                        )}
                        <button
                          type="button"
                          className="aa-lens-clear"
                          onClick={clearDossier}
                          title="Clear dossier"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                    {dossierWarnings[0] && (
                      <p className="aa-brain-dossier-warn">{dossierWarnings[0]}</p>
                    )}
                    <div className="aa-brain-dossier-body">
                      {!dossierLoading &&
                        dossierSections.map(group => (
                          <div key={group.section} className="aa-brain-dossier-section">
                            <div className="aa-brain-dossier-section-label">{group.label}</div>
                            <ul className="aa-brain-dossier-list">
                              {group.hits.map(hit => (
                                <li key={hit.id} className="aa-brain-dossier-row">
                                  <div className="aa-brain-dossier-row-main">
                                    <div className="aa-brain-dossier-row-title">
                                      <span>{hit.title}</span>
                                      <span className="aa-brain-dossier-kind">
                                        {brainKindLabel(hit.kind)}
                                      </span>
                                    </div>
                                    <div className="aa-brain-dossier-location">
                                      {hit.locationUrl ? (
                                        <a
                                          href={hit.locationUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          {hit.location}
                                        </a>
                                      ) : (
                                        hit.location
                                      )}
                                    </div>
                                    {hit.excerpt && (
                                      <p className="aa-brain-dossier-excerpt">{hit.excerpt}</p>
                                    )}
                                  </div>
                                  {(hit.url || hit.locationUrl) && (
                                    <a
                                      className="aa-brain-dossier-open"
                                      href={hit.url || hit.locationUrl || '#'}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <ExternalLink size={12} />
                                      Open
                                    </a>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      {!dossierLoading && !dossierError && brainSubject && (
                        <div className="aa-brain-dossier-empty-notes">
                          {(dossierBySection?.github ?? 0) === 0 && (
                            <p>No Company Brain (GitHub) files matched for this {brainSubject.type}.</p>
                          )}
                          {(dossierBySection?.crm ?? 0) === 0 && (
                            <p>No CRM digests found for this {brainSubject.type}.</p>
                          )}
                          {dossierMeetingCount === 0 &&
                            (brainSubject.type === 'lead' || brainSubject.type === 'deal') && (
                              <p>
                                No meeting notes in CRM for this {brainSubject.type}.
                              </p>
                            )}
                          {(dossierBySection?.activity ?? 0) === 0 && (
                            <p>No recent agent retrievals cited for this subject.</p>
                          )}
                        </div>
                      )}
                    </div>
                  </section>
                )}
              </div>
            </section>
          </div>
        )}

        {view === 'coordination' && (
          <div className="aa-builds">
            <div className="aa-builds-head">
              <div className="aa-directory-intro">
                <h2>Agent coordination</h2>
                <p>
                  Feature and capability requests from Mac-mini agents via{' '}
                  <code>reportRequests</code>, merged with historical GitHub PRs from the CRM
                  repo. Report as soon as an agent is blocked — use <code>status: proposed</code>{' '}
                  before a PR exists.
                </p>
                <a className="aa-doc-link" href={REPORTING_DOC_URL} target="_blank" rel="noopener noreferrer">
                  <ExternalLink size={12} />
                  Reporting guide
                </a>
              </div>
              <div className="aa-stream-filters aa-builds-filters">
                <select
                  className="aa-select"
                  value={agentFilter}
                  onChange={e => setAgentFilter(e.target.value)}
                  aria-label="Filter by agent"
                >
                  <option value="all">All agents</option>
                  {Array.from(new Set(mergedBuilds.map(b => b.agentId).filter(Boolean))).map(agentId => (
                    <option key={agentId} value={agentId}>
                      {metaFor(agentId).label}
                    </option>
                  ))}
                </select>
                <select
                  className="aa-select"
                  value={buildStatusFilter}
                  onChange={e => setBuildStatusFilter(e.target.value)}
                  aria-label="Filter by build status"
                >
                  <option value="open">Open (proposed + PR open)</option>
                  <option value="all">All statuses</option>
                  <option value="proposed">Proposed</option>
                  <option value="pr_open">PR open</option>
                  <option value="pr_merged">Merged</option>
                  <option value="implemented">Live</option>
                  <option value="pr_closed">PR closed</option>
                  <option value="declined">Declined</option>
                </select>
                <span className="aa-stream-count">
                  {filteredBuilds.length} coordination request{filteredBuilds.length === 1 ? '' : 's'}
                  {openBuildCount > 0 ? ` · ${openBuildCount} open` : ''}
                  {githubBuildsLoading ? ' · loading GitHub…' : ''}
                </span>
              </div>
            </div>

            {githubBuildsError && (
              <div className="aa-builds-github-error">
                GitHub history unavailable: {githubBuildsError}
              </div>
            )}

            {filteredBuilds.length === 0 && !githubBuildsLoading && (
              <div className="aa-stream-empty">
                <GitPullRequest size={20} />
                <p>
                  No coordination requests match. Agents report via <code>reportRequests</code>;
                  historical PRs load from GitHub when configured.
                </p>
              </div>
            )}

            <div className="aa-build-rows">
              {filteredBuilds.map(b => {
                const expanded = expandedBuildId === b.id;
                const open = OPEN_BUILD_STATUSES.has(b.status);
                const kindLabel = BUILD_KIND_LABELS[b.kind] || b.kind;
                const statusLabel = BUILD_STATUS_LABELS[b.status] || b.status;
                const agentLabel = b.agentId ? metaFor(b.agentId).label : 'Unattributed';
                const agentMeta = b.agentId ? metaFor(b.agentId) : FALLBACK_META;
                const ask = buildRequestAsk(b);
                return (
                  <div key={b.id} className={`aa-event aa-build-card${expanded ? ' expanded' : ''}`}>
                    <button
                      type="button"
                      className="aa-event-row aa-build-row"
                      onClick={() => setExpandedBuildId(expanded ? null : b.id)}
                      aria-expanded={expanded}
                    >
                      <span className="aa-event-chevron">
                        {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                      </span>
                      <span
                        className="aa-event-time"
                        title={b.updatedAt ? b.updatedAt.toDate().toLocaleString() : ''}
                      >
                        {relativeTime(b.updatedAt || b.proposedAt)}
                      </span>
                      <span className="aa-event-agent" style={{ ['--aa-accent' as any]: agentMeta.accent }}>
                        {agentMeta.icon}
                        <span>{agentLabel}</span>
                      </span>
                      <span className="aa-build-kind">{kindLabel}</span>
                      <span className="aa-build-title">{b.title}</span>
                      <span
                        className={`aa-event-status aa-bstatus-${b.status}${open ? ' aa-bstatus-open' : ''}`}
                      >
                        {statusLabel}
                      </span>
                      {b.prUrl && (
                        <a
                          className="aa-build-pr-link"
                          href={b.prUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          title="Open pull request"
                        >
                          <GitPullRequest size={12} />
                          {b.prNumber != null ? `#${b.prNumber}` : 'PR'}
                        </a>
                      )}
                    </button>
                    <div className="aa-build-ask-block">
                      <div className="aa-build-ask-head">
                        <span className="aa-build-ask-label">What they&apos;re asking</span>
                        {!ask.fromAgent && (
                          <span className="aa-build-ask-hint">No summary from agent</span>
                        )}
                      </div>
                      <p className={`aa-build-ask${expanded ? ' expanded' : ''}`}>{ask.text}</p>
                      {!ask.fromAgent && (
                        <p className="aa-build-ask-note">
                          Re-report with a <code>summary</code> field explaining what is blocked and what
                          CRM change is needed.
                        </p>
                      )}
                      <div className="aa-build-inline-meta">
                        <span className="aa-build-inline-chip">
                          <span className="aa-build-meta-label">ID</span>
                          <code>{b.requestId}</code>
                        </span>
                        {b.host && (
                          <span className="aa-build-inline-chip">
                            <span className="aa-build-meta-label">Host</span>
                            <span>{b.host}</span>
                          </span>
                        )}
                        {b.repo && (
                          <span className="aa-build-inline-chip">
                            <span className="aa-build-meta-label">Repo</span>
                            <span>{b.repo}</span>
                          </span>
                        )}
                        {b.author && (
                          <span className="aa-build-inline-chip">
                            <span className="aa-build-meta-label">Author</span>
                            <span>{b.author}</span>
                          </span>
                        )}
                      </div>
                    </div>
                    {expanded && (
                      <div className="aa-event-detail aa-build-detail">
                        <div className="aa-build-meta">
                          <div>
                            <span className="aa-build-meta-label">Kind</span>
                            <span>{kindLabel}</span>
                          </div>
                          <div>
                            <span className="aa-build-meta-label">Status</span>
                            <span>{statusLabel}</span>
                          </div>
                          {b.source && (
                            <div>
                              <span className="aa-build-meta-label">Source</span>
                              <span>{b.source === 'github' ? 'GitHub PR' : 'Agent report'}</span>
                            </div>
                          )}
                          <div>
                            <span className="aa-build-meta-label">Proposed</span>
                            <span>
                              {b.proposedAt ? b.proposedAt.toDate().toLocaleString() : '—'}
                            </span>
                          </div>
                          <div>
                            <span className="aa-build-meta-label">Updated</span>
                            <span>
                              {b.updatedAt ? b.updatedAt.toDate().toLocaleString() : '—'}
                            </span>
                          </div>
                        </div>
                        {b.prUrl && (
                          <a
                            className="aa-doc-link"
                            href={b.prUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ExternalLink size={12} />
                            View pull request
                            {b.prNumber != null ? ` #${b.prNumber}` : ''}
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {view === 'directory' && (
          <div className="aa-directory">
            <div className="aa-directory-head">
              <div className="aa-directory-intro">
                <h2>Agent function directory</h2>
                <p>
                  Every action each agent can call against the CRM, with what it does and how often
                  it was used this month. Generated from the agent registry (
                  <a href="/docs/agents/catalog.json" target="_blank" rel="noreferrer">
                    catalog.json
                  </a>
                  ).
                </p>
              </div>
              <div className="aa-directory-search">
                <Search size={14} />
                <input
                  type="text"
                  placeholder="Search functions…"
                  value={directorySearch}
                  onChange={e => setDirectorySearch(e.target.value)}
                />
              </div>
            </div>
            {instructionDocs.length > 0 && (
              <div className="aa-instruction-docs">
                <h3>Bot instruction docs</h3>
                <p>
                  Point an external planner (including the Grok travel bot) at these URLs. They
                  describe the job queue and the JSON the bot must return.
                </p>
                <ul>
                  {instructionDocs.map(doc => {
                    const url =
                      doc.id === 'travelPlannerInput'
                        ? doc.jsonUrl || PUBLIC_PLANNER_INSTRUCTION_URL
                        : doc.jsonUrl || `${window.location.origin}/docs/agents/${doc.id}.json`;
                    const copied = copiedInstructionId === doc.id;
                    return (
                      <li key={doc.id} className={doc.id === 'travelPlannerInput' ? 'featured' : ''}>
                        <div>
                          <strong>{doc.title}</strong>
                          <p>{doc.description}</p>
                          <code>{url}</code>
                        </div>
                        <div className="aa-instruction-docs-actions">
                          <button
                            type="button"
                            className="aa-dir-guide-link"
                            onClick={() => {
                              void navigator.clipboard.writeText(url).then(() => {
                                setCopiedInstructionId(doc.id);
                                window.setTimeout(() => setCopiedInstructionId(null), 1600);
                              });
                            }}
                          >
                            {copied ? <Check size={12} /> : <Copy size={12} />}
                            <span>{copied ? 'Copied' : 'Copy URL'}</span>
                          </button>
                          <a
                            className="aa-dir-guide-link"
                            href={localDocPath(url)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <ExternalLink size={12} />
                            <span>Open</span>
                          </a>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {directoryError && (
              <div className="aa-stream-empty">
                <BookOpen size={20} />
                <p>Could not load the agent catalog: {directoryError}</p>
              </div>
            )}
            {filteredDirectory.map(api => {
              const meta = metaFor(api.id);
              const counts = actionCounts.get(api.id) || {};
              return (
                <section key={api.id} className="aa-dir-api" style={{ ['--aa-accent' as any]: meta.accent }}>
                  <div className="aa-dir-api-head">
                    <span className="aa-agent-icon">{meta.icon}</span>
                    <div className="aa-dir-api-titles">
                      <h3>{api.name}</h3>
                      <p>{api.description}</p>
                    </div>
                    <a
                      className="aa-dir-guide-link"
                      href={localDocPath(`/docs/agents/${api.id}.json`)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <ExternalLink size={12} />
                      <span>Guide</span>
                    </a>
                  </div>
                  <div className="aa-dir-actions">
                    {api.actions.map(action => {
                      const key = `${api.id}:${action.name}`;
                      const expanded = expandedAction === key;
                      const count = counts[action.name] || 0;
                      return (
                        <div key={key} className={`aa-dir-action${expanded ? ' expanded' : ''}`}>
                          <button
                            type="button"
                            className="aa-dir-action-row"
                            onClick={() => setExpandedAction(expanded ? null : key)}
                          >
                            <span className="aa-event-chevron">
                              {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                            </span>
                            <code className="aa-dir-action-name">{action.name}</code>
                            <span className="aa-dir-action-desc">{action.description || '—'}</span>
                            <span className={`aa-dir-action-count${count > 0 ? ' used' : ''}`} title="Calls this month">
                              {count > 0 ? `${count} this mo` : 'unused'}
                            </span>
                          </button>
                          {expanded && (
                            <div className="aa-dir-action-detail">
                              {action.params?.length ? (
                                <table className="aa-dir-params">
                                  <thead>
                                    <tr>
                                      <th>Param</th>
                                      <th>Type</th>
                                      <th>Required</th>
                                      <th>Description</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {action.params.map(p => (
                                      <tr key={p.name}>
                                        <td><code>{p.name}</code></td>
                                        <td>{p.type || '—'}</td>
                                        <td>{p.required ? 'yes' : 'no'}</td>
                                        <td>{p.description || '—'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              ) : (
                                <p className="aa-dir-noparams">No parameters.</p>
                              )}
                              {action.returns && (
                                <p className="aa-dir-returns">
                                  <span>Returns</span> <code>{action.returns}</code>
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Agent drill-in slide-over ── */}
      {drillAgent && (
        <div className="aa-drill-overlay" onClick={closeDrill}>
          <aside
            className="aa-drill"
            style={{ ['--aa-accent' as any]: metaFor(drillAgent.agentId).accent }}
            onClick={e => e.stopPropagation()}
          >
            <button className="aa-drill-close" onClick={closeDrill}>
              <X size={18} />
            </button>
            <div className="aa-drill-head">
              <span className="aa-agent-icon aa-drill-icon">{metaFor(drillAgent.agentId).icon}</span>
              <div>
                <h2>{metaFor(drillAgent.agentId).label}</h2>
                <span className="aa-drill-id">{drillAgent.agentId}</span>
              </div>
            </div>

            <div className="aa-drill-stats">
              <div className="aa-drill-stat">
                <span className="aa-drill-stat-num">{drillAgent.callsToday}</span>
                <span className="aa-drill-stat-label">calls today</span>
              </div>
              <div className="aa-drill-stat">
                <span className="aa-drill-stat-num">{drillAgent.callsMonth}</span>
                <span className="aa-drill-stat-label">calls MTD</span>
              </div>
              <div className="aa-drill-stat">
                <span className="aa-drill-stat-num">{drillAgent.errorsMonth}</span>
                <span className="aa-drill-stat-label">errors MTD</span>
              </div>
              <div className="aa-drill-stat">
                <span className="aa-drill-stat-num">
                  {fmtUsd(usageAgents[drillAgent.agentId]?.costUsd || 0)}
                </span>
                <span className="aa-drill-stat-label">cost MTD</span>
              </div>
            </div>

            <h3 className="aa-drill-label">Actions this month</h3>
            <div className="aa-drill-actions">
              {Object.entries(actionCounts.get(drillAgent.agentId) || {})
                .sort(([, a], [, b]) => b - a)
                .map(([action, count]) => {
                  const max = Math.max(
                    ...Object.values(actionCounts.get(drillAgent.agentId) || { x: 1 }),
                    1,
                  );
                  return (
                    <div key={action} className="aa-drill-action">
                      <code>{action}</code>
                      <div className="aa-drill-bar-track">
                        <div className="aa-drill-bar" style={{ width: `${(count / max) * 100}%` }} />
                      </div>
                      <span>{count}</span>
                    </div>
                  );
                })}
              {Object.keys(actionCounts.get(drillAgent.agentId) || {}).length === 0 && (
                <p className="aa-drill-empty">No calls recorded this month.</p>
              )}
            </div>

            {usageAgents[drillAgent.agentId] && (
              <>
                <h3 className="aa-drill-label">Usage (self-reported)</h3>
                <div className="aa-drill-usage">
                  <span>{usageAgents[drillAgent.agentId].runs} runs</span>
                  <span>{(usageAgents[drillAgent.agentId].inputTokens || 0).toLocaleString()} in-tokens</span>
                  <span>{(usageAgents[drillAgent.agentId].outputTokens || 0).toLocaleString()} out-tokens</span>
                </div>
              </>
            )}

            <h3 className="aa-drill-label">Raw API stream</h3>
            <div className="aa-drill-raw">
              {drillEvents.length === 0 && (
                <p className="aa-drill-empty">
                  No events in the loaded window yet — the tail is loading or this skill has been
                  quiet.
                </p>
              )}
              {drillEvents.map(e => {
                const expanded = expandedEventId === e.id;
                const poll = isPollEvent(e.action, e.source);
                const skillIds = reportedSkillIds(e.paramsPreview);
                const missing = isMissingSkill(e.action, e.paramsPreview, poll, e.source);
                return (
                  <div key={e.id} className={`aa-event${expanded ? ' expanded' : ''}${missing ? ' missing-skill' : ''}`}>
                    <button
                      type="button"
                      className="aa-event-row"
                      onClick={() => setExpandedEventId(expanded ? null : e.id)}
                    >
                      <span className="aa-event-chevron">
                        {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                      </span>
                      <span className="aa-event-time" title={e.ts ? e.ts.toDate().toLocaleString() : ''}>
                        {clockTime(e.ts)}
                      </span>
                      <code className="aa-event-action">{e.action}</code>
                      {skillIds.map(id => (
                        <span key={id} className="aa-event-skill" title="skillsUsed">
                          {skillById(id)?.label || id}
                        </span>
                      ))}
                      {!skillIds.length && hasReportedSkill(e.paramsPreview) && (
                        <span className="aa-event-skill" title="skillsUsed (unmapped)">
                          {String(e.paramsPreview?.skillsUsed || e.paramsPreview?.skill)}
                        </span>
                      )}
                      {missing && (
                        <span className="aa-event-skill-missing" title="No skillsUsed on this action">
                          <AlertTriangle size={10} />
                          no skill
                        </span>
                      )}
                      {e.host && (
                        <span className="aa-event-host" title={`Worker host: ${e.host}`}>
                          <Monitor size={11} />
                          <span>{e.host}</span>
                        </span>
                      )}
                      <span className={`aa-event-status aa-status-${e.status}`}>{e.status}</span>
                      <span className="aa-event-latency">{e.latencyMs}ms</span>
                    </button>
                    {expanded && (
                      <div className="aa-event-detail">
                        <div className="aa-event-detail-grid">
                          <span>HTTP</span>
                          <code>{e.httpStatus}</code>
                          <span>Source</span>
                          <code>{sourceLabel(e.source)}</code>
                          {e.apiAgentId && e.apiAgentId !== drillAgent.agentId && (
                            <>
                              <span>API route</span>
                              <code>{e.apiAgentId}</code>
                            </>
                          )}
                          <span>Auth</span>
                          <code>{e.keyKind}</code>
                          <span>Host</span>
                          <code>{e.host || 'not reported'}</code>
                          <span>When</span>
                          <code>{e.ts ? e.ts.toDate().toLocaleString() : '—'}</code>
                        </div>
                        {e.paramsPreview && Object.keys(e.paramsPreview).length > 0 && (
                          <>
                            <h4 className="aa-event-params-label">Request params</h4>
                            <div className="aa-event-params">
                              {Object.entries(e.paramsPreview).map(([k, v]) => (
                                <div key={k} className="aa-event-param">
                                  <code className="aa-param-key">{k}</code>
                                  <span className="aa-param-value">{String(v)}</span>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="aa-drill-links">
              <a href={localDocPath(`/docs/agents/${REGISTRY_DOC_ALIASES[drillAgent.agentId] || drillAgent.agentId}.json`)} target="_blank" rel="noreferrer">
                <ExternalLink size={12} />
                <span>Registry guide</span>
              </a>
              <button
                type="button"
                onClick={() => {
                  setDrillAgentId(null);
                  setView('directory');
                  setDirectorySearch(metaFor(drillAgent.agentId).label);
                }}
              >
                <BookOpen size={12} />
                <span>Functions directory</span>
              </button>
              <button type="button" onClick={() => navigate('/agent-harness')}>
                <Sparkles size={12} />
                <span>Agent Harness</span>
              </button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default AgentActivityPage;
